import React, { useEffect, useState } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, Alert, UncontrolledTooltip } from "reactstrap"
import gql from "graphql-tag"
import Select from "react-select"
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useQuery, useMutation } from "@apollo/client"

import { PeopleSchema, VehicleSchema } from "./IncidentModalsSchema"
import SearchPlaces from "../../Material/SearchPlaces"
import './Modals.css'
import AddRepresentativeModal from './AddRepresentativeModal'
import { SEAT_ARRANGEMENT } from "../../Constants"

export const CreateIncidentPartyVehicle = gql`
  mutation createIncidentPartyVehicle($input: CreateIncidentPartyVehicleMutationInput!) {
    createIncidentPartyVehicle(input: $input) {
      ok
      errors { 
        field 
        messages
      }
    }
  }
`

export const CreateIncidentPartyPeople = gql`
  mutation createIncidentPartyPeople($input: CreateIncidentPartyPeopleMutationInput!) {
    createIncidentPartyPeople(input: $input) {
      ok
      errors { 
        field 
        messages
      }
    }
  }
`

const INJURIES_ENUM_QUERY = gql`
  query GetInjuriesChoices {
    injuriesChoices: __type(name: "IncidentsIncidentPartyPeopleInjuriesChoices") {
      enumValues { 
        name 
        description
      }
    }
  }
`

const ALL_INCIDENT_PARTY_REPRESENTATIVES = gql`
  query AllIncidentPartyRepresentatives {
    allIncidentPartyRepresentatives {
      edges {
        node {
          id
          name
          businessName
        }
      }
    }
  }
`

// Add fetchMakes and fetchModels functions (same as AddVehicleModal)
const fetchMakes = async () => {
  const res = await fetch("https://vpic.nhtsa.dot.gov/api/vehicles/getallmakes?format=json")
  const data = await res.json()
  return data.Results.map(m => ({ value: m.Make_Name, label: m.Make_Name }))
}

const fetchModels = async (make) => {
  if (!make) return []
  const res = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/getmodelsformake/${encodeURIComponent(make)}?format=json`)
  const data = await res.json()
  return data.Results.map(m => ({ value: m.Model_Name, label: m.Model_Name }))
}

export default function AddPartyEntityModal({ isOpen, toggle, entity, partyId, refetchQuery, passengerPosition }) {
  const isVehicle = entity === "VEHICLE" ? true : false
  const isPassenger = entity === "VEHICLE_PASSENGER"

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const [createVehicle] = useMutation(CreateIncidentPartyVehicle)
  const [createPeople] = useMutation(CreateIncidentPartyPeople)

  const { data: injuriesData, loading: injuriesLoading } = useQuery(INJURIES_ENUM_QUERY, { skip: isVehicle })
  const injuryOptions = injuriesData?.injuriesChoices?.enumValues?.map(opt => ({
    value: opt.name,
    label: opt.description || opt.name
  })) || []

  const vehicleDefaults = {
    vin: "",
    make: "",
    model: "",
    year: "",
    plateNumber: "",
    damageDescription: "",
    insuranceCompany: "",
    policyNumber: "",
  }

  const peopleDefaults = {
    role: entity || "VEHICLE_DRIVER",
    name: "",
    phoneNumber: "",
    email: "",
    address: "",
    driverLicense: "",
    injuries: "",
    statement: "",
    isBusinessEntity: false,
    passengerPosition: passengerPosition,
  }

  // Add state for make/model dropdowns
  const [makeOptions, setMakeOptions] = useState([])
  const [modelOptions, setModelOptions] = useState([])
  const [loadingModels, setLoadingModels] = useState(false)

  const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    resolver: yupResolver(isVehicle ? VehicleSchema : PeopleSchema),
    defaultValues: isVehicle ?
      vehicleDefaults : peopleDefaults,
  })


  // Watch make for vehicle
  const make = watch('make')

  useEffect(() => {
    if (!isOpen) return
    reset(isVehicle ? vehicleDefaults : { ...peopleDefaults, role: entity, passengerPosition: passengerPosition })
    setError(null)
    if (isVehicle) {
      fetchMakes().then(setMakeOptions)
      setModelOptions([])
    }
  }, [isOpen, entity, isVehicle, reset])

  useEffect(() => {
    if (isVehicle) {
      if (make) {
        setLoadingModels(true)
        fetchModels(make).then((models) => {
          setModelOptions(models)
          setLoadingModels(false)
        })
      } else {
        setModelOptions([])
      }
    }
  }, [make, isVehicle])


  const onSubmit = async (formData) => {
    setError(null)
    setLoading(true)
    if (isVehicle) {
      const input = {
        incidentPartyId: partyId,
        ...formData
      }
      try {
        const res = await createVehicle({ variables: { input } })
        if (res.data?.createIncidentPartyVehicle?.ok) {
          if (refetchQuery) refetchQuery()
          reset()
          toggle()
        }
        if (res.data?.createIncidentPartyVehicle?.errors?.[0]?.messages?.[0]) {
          setError(res.data.createIncidentPartyVehicle.errors[0].messages[0])
        }
      } catch (err) {
        setError("An unexpected error occurred. " + err.message)
      } finally {
        setLoading(false)
      }
    } else {
      const input = {
        incidentPartyId: partyId,
        ...formData,
        ...(passengerPosition ? { passengerPosition } : {}),
      }
      try {
        const res = await createPeople({ variables: { input } })
        if (res.data?.createIncidentPartyPeople?.ok) {
          if (refetchQuery)
            refetchQuery()
          reset()
          toggle()
        }
        if (res.data?.createIncidentPartyPeople?.errors?.[0]?.messages?.[0]) {
          setError(res.data.createIncidentPartyPeople.errors[0].messages[0])
        }
      } catch (err) {
        setError("An unexpected error occurred. " + err.message)
      } finally {
        setLoading(false)
      }
    }
  }

  const [representativeOptions, setRepresentativeOptions] = useState([])
  const [isRepresentativeModalOpen, setRepresentativeModalOpen] = useState(false)
  const { data: representativesData, refetch: refetchRepresentatives } = useQuery(ALL_INCIDENT_PARTY_REPRESENTATIVES)
  useEffect(() => {
    const options = representativesData?.allIncidentPartyRepresentatives?.edges?.map(edge => ({
      label: edge.node.businessName + " - " + edge.node.name,
      value: edge.node.id,
    })) || []
    setRepresentativeOptions(options)
  }, [representativesData])

  const toggleRepresentativeModal = () => setRepresentativeModalOpen(!isRepresentativeModalOpen)

  const handleNewRepresentative = (newRepresentative) => {
    const newOption = { label: newRepresentative.name, value: newRepresentative.id }
    setRepresentativeOptions((prevOptions) => {
      const exists = prevOptions.some(option => option.value === newOption.value)
      if (!exists) {
        return [...prevOptions, newOption]
      }
      return prevOptions
    })
    setValue("representativeId", newOption.value)
  }

  return (
    <Modal isOpen={isOpen} className="add-party-entity-modal modal-dialog-centered" size="lg">
      <ModalHeader>{entity === "VEHICLE" ? "Add Party Vehicle" : "Add Party Person"}</ModalHeader>
      <ModalBody>
        {error && <Row><Col><Alert color="danger">{error}</Alert></Col></Row>}
        <Form onSubmit={handleSubmit(onSubmit)}>
          {isVehicle ? (
            <>
              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="make">Make</Label>
                    <Controller
                      name="make"
                      control={control}
                      render={({ field }) => (
                        <Select
                          inputId="make"
                          options={makeOptions}
                          placeholder="Select Make"
                          value={makeOptions.find(opt => opt.value === field.value) || null}
                          onChange={opt => field.onChange(opt ? opt.value : '')}
                          onBlur={field.onBlur}
                          isClearable
                          isLoading={makeOptions.length === 0}
                        />
                      )}
                    />
                    {errors.make && <div className="invalid-feedback d-block">{errors.make.message}</div>}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="model">Model</Label>
                    <Controller
                      name="model"
                      control={control}
                      render={({ field }) => (
                        <Select
                          inputId="model"
                          options={modelOptions}
                          placeholder={make ? "Select Model" : "Select Make First"}
                          value={modelOptions.find(opt => opt.value === field.value) || null}
                          onChange={opt => field.onChange(opt ? opt.value : '')}
                          onBlur={field.onBlur}
                          isClearable
                          isDisabled={!make}
                          isLoading={loadingModels}
                        />
                      )}
                    />
                    {errors.model && <div className="invalid-feedback d-block">{errors.model.message}</div>}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="year">Year</Label>
                    <Controller
                      name="year"
                      control={control}
                      render={({ field }) => (
                        <Input id="year" placeholder="e.g. 2020" invalid={!!errors.year} {...field} />
                      )}
                    />
                    {errors.year && <div className="invalid-feedback d-block">{errors.year.message}</div>}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="vin">VIN</Label>
                    <Controller
                      name="vin"
                      control={control}
                      render={({ field }) => (
                        <Input id="vin" placeholder="e.g. 1HGCM82633A123456" invalid={!!errors.vin} {...field} />
                      )}
                    />
                    {errors.vin && <div className="invalid-feedback d-block">{errors.vin.message}</div>}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="plateNumber">License Plate</Label>
                    <Controller
                      name="plateNumber"
                      control={control}
                      render={({ field }) => (
                        <Input id="plateNumber" placeholder="e.g. ABC1234" invalid={!!errors.plateNumber} {...field} />
                      )}
                    />
                    {errors.plateNumber && <div className="invalid-feedback d-block">{errors.plateNumber.message}</div>}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label htmlFor="policyNumber">Policy Number</Label>
                    <Controller
                      name="policyNumber"
                      control={control}
                      render={({ field }) => (
                        <Input id="policyNumber" placeholder="e.g. 123456789" invalid={!!errors.policyNumber} {...field} />
                      )}
                    />
                    {errors.policyNumber && <div className="invalid-feedback d-block">{errors.policyNumber.message}</div>}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup className="mb-3">
                    <Label htmlFor="insuranceCompany">Insurance Company</Label>
                    <Controller
                      name="insuranceCompany"
                      control={control}
                      render={({ field }) => (
                        <Input id="insuranceCompany" placeholder="e.g. ABC Insurance" invalid={!!errors.insuranceCompany} {...field} />
                      )}
                    />
                    {errors.insuranceCompany && <div className="invalid-feedback d-block">{errors.insuranceCompany.message}</div>}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <FormGroup className="mb-3">
                    <Label htmlFor="damageDescription">Damage Description</Label>
                    <Controller
                      name="damageDescription"
                      control={control}
                      render={({ field }) => (
                        <Input type="textarea" id="damageDescription" placeholder="Describe any damage" invalid={!!errors.damageDescription} {...field} />
                      )}
                    />
                    {errors.damageDescription && <div className="invalid-feedback d-block">{errors.damageDescription.message}</div>}
                  </FormGroup>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <FormGroup className="mb-3">
                <Label htmlFor="role">Role</Label>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Input id="role" type="text" disabled value={(field.value || entity).replace("_", " ")} />
                  )}
                />
              </FormGroup>

              <FormGroup className="mb-3">
                <Label htmlFor="name">Name</Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input id="name" placeholder="e.g. John Doe" invalid={!!errors.name} {...field} />
                  )}
                />
                {errors.name && <div className="invalid-feedback d-block">{errors.name.message}</div>}
              </FormGroup>

              {isPassenger && !passengerPosition && (
                <FormGroup className="mb-3">
                  <Label htmlFor="passengerPosition">Seat Position</Label>
                  <Controller
                    name="passengerPosition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        classNamePrefix="bos-select"
                        options={SEAT_ARRANGEMENT}
                        value={SEAT_ARRANGEMENT.find(option => option.value === field.value) || null}
                        onChange={(option) => field.onChange(option?.value || "")}
                        placeholder="Select seat"
                      />
                    )}
                  />
                </FormGroup>
              )}

              <FormGroup className="mb-3">
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <Input id="phoneNumber" placeholder="e.g. +11234567890" invalid={!!errors.phoneNumber} {...field} />
                  )}
                />
                {errors.phoneNumber && <div className="invalid-feedback d-block">{errors.phoneNumber.message}</div>}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label htmlFor="email">Email</Label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input id="email" type="email" placeholder="e.g. john.doe@example.com" invalid={!!errors.email} {...field} />
                  )}
                />
                {errors.email && <div className="invalid-feedback d-block">{errors.email.message}</div>}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label htmlFor="driverLicense">Driver License</Label>
                <Controller
                  name="driverLicense"
                  control={control}
                  render={({ field }) => (
                    <Input id="driverLicense" placeholder="e.g. D1234567" invalid={!!errors.driverLicense} {...field} />
                  )}
                />
                {errors.driverLicense && <div className="invalid-feedback d-block">{errors.driverLicense.message}</div>}
              </FormGroup>
              {!isVehicle && (
                <FormGroup>
                  <Label for="representative">Representative</Label>
                  <div className="d-flex align-items-end gap-2 representative-field-row">
                    <div className="flex-grow-1 representative-field-input">
                      <Controller
                        name="representativeId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            className="bos-custom-select"
                            classNamePrefix="bos-select"
                            {...field}
                            options={representativeOptions}
                            isClearable
                            placeholder="Select Representative"
                            onChange={(option) => field.onChange(option ? option.value : null)}
                            value={representativeOptions.find(opt => opt.value === field.value) || null}
                          />
                        )}
                      />
                    </div>
                    <Button className="add-rep-btn" id="representativeAddTooltip" onClick={toggleRepresentativeModal}><i className="fa fa-plus" /></Button>
                    <UncontrolledTooltip target="representativeAddTooltip" placement="top">Add New Representative</UncontrolledTooltip>
                  </div>
                </FormGroup>
              )}
              <FormGroup className="mb-3">
                <Label htmlFor="address">Address</Label>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <SearchPlaces
                      value={field.value ?? ""}
                      searchType="address"
                      onSaveAction={(item) => field.onChange(item?.name ?? "")}
                    />
                  )}
                />
                {errors.address && <div className="invalid-feedback d-block">{errors.address.message}</div>}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label htmlFor="injuries">Injuries</Label>
                <Controller
                  name="injuries"
                  control={control}
                  render={({ field }) => (
                    <Select
                      inputId="injuries"
                      isLoading={injuriesLoading}
                      options={injuryOptions}
                      placeholder="Select injury status"
                      isClearable
                      classNamePrefix="react-select"
                      onChange={(sel) => field.onChange(sel ? sel.value : "")}
                      value={injuryOptions.find((o) => o.value === field.value) || null}
                    />
                  )}
                />
                {errors.injuries && <div className="invalid-feedback d-block">{errors.injuries.message}</div>}
              </FormGroup>

              <FormGroup check className="mb-3">
                <Controller
                  name="isBusinessEntity"
                  control={control}
                  render={({ field }) => (
                    <Input type="checkbox" id="isBusinessEntity" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} />
                  )}
                />
                <Label check htmlFor="isBusinessEntity" className="ms-2">Is Business Entity</Label>
                {errors.isBusinessEntity && <div className="invalid-feedback d-block">{errors.isBusinessEntity.message}</div>}
              </FormGroup>

              <FormGroup className="mb-3">
                <Label htmlFor="statement">Statement</Label>
                <Controller
                  name="statement"
                  control={control}
                  render={({ field }) => (
                    <Input type="textarea" id="statement" placeholder="Enter statement" invalid={!!errors.statement} {...field} />
                  )}
                />
                {errors.statement && <div className="invalid-feedback d-block">{errors.statement.message}</div>}
              </FormGroup>
            </>
          )}

          <button type="submit" className="d-none" />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>Cancel</Button>
        <Button color="primary" onClick={handleSubmit(onSubmit)} disabled={loading}>{loading ? "loading..." : "Add"}</Button>
      </ModalFooter>

      <AddRepresentativeModal
        isOpen={isRepresentativeModalOpen}
        toggle={toggleRepresentativeModal}
        refetchRepresentatives={refetchRepresentatives}
        onCreate={handleNewRepresentative}
      />
    </Modal>
  )
}
